import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, Send, Loader2 } from "lucide-react";
import { useLang } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CONTACT_EMAIL = "audifonosamplisoon@gmail.com";

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    centre: "",
    service: "",
    preferred_date: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.service || !form.centre) {
      toast.error(t.contact.error);
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      if (!payload.preferred_date) delete payload.preferred_date;
      if (!payload.message) delete payload.message;
      await axios.post(`${API}/appointments`, payload);
      toast.success(t.contact.success);
      setForm({ name: "", phone: "", email: "", centre: "", service: "", preferred_date: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error(t.contact.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contacto" className="py-20 md:py-28 bg-white" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-sm font-bold uppercase tracking-widest text-sky-700">
            {t.contact.kicker}
          </p>
          <h2 className="mt-3 text-4xl md:text-5xl font-extrabold tracking-tight text-blue-950 leading-[1.1]">
            {t.contact.title}
          </h2>
          <p className="mt-5 text-lg text-slate-700">{t.contact.subtitle}</p>
        </div>

        {/* Form first */}
        <div className="max-w-3xl mx-auto mt-10">
          <form
            onSubmit={handleSubmit}
            className="bg-slate-50 border border-slate-200 rounded-3xl p-7 md:p-10 shadow-sm"
            data-testid="appointment-form"
          >
            <div className="grid md:grid-cols-2 gap-5">
              <Field id="name" label={t.contact.labels.name} required>
                <Input
                  id="name"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  placeholder={t.contact.placeholders.name}
                  className="h-14 text-base bg-white"
                  data-testid="form-name-input"
                />
              </Field>
              <Field id="phone" label={t.contact.labels.phone} required>
                <Input
                  id="phone"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  placeholder={t.contact.placeholders.phone}
                  className="h-14 text-base bg-white"
                  data-testid="form-phone-input"
                />
              </Field>
              <Field id="email" label={t.contact.labels.email}>
                <Input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  placeholder={t.contact.placeholders.email}
                  className="h-14 text-base bg-white"
                  data-testid="form-email-input"
                />
              </Field>
              <Field id="preferred_date" label={t.contact.labels.date}>
                <Input
                  id="preferred_date"
                  type="date"
                  value={form.preferred_date}
                  onChange={(e) => update("preferred_date", e.target.value)}
                  className="h-14 text-base bg-white"
                  data-testid="form-date-input"
                />
              </Field>
              <div className="md:col-span-2">
                <Field id="centre" label="Centro" required>
                  <Select value={form.centre} onValueChange={(v) => update("centre", v)}>
                    <SelectTrigger id="centre" className="h-14 text-base bg-white" data-testid="form-centre-trigger">
                      <SelectValue placeholder="Selecciona un centro" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="El Campello (Alicante)" data-testid="form-centre-campello">El Campello (Alicante)</SelectItem>
                      <SelectItem value="Torrellano (Elche)" data-testid="form-centre-torrellano">Torrellano (Elche)</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <div className="md:col-span-2">
                <Field id="service" label={t.contact.labels.service} required>
                  <Select value={form.service} onValueChange={(v) => update("service", v)}>
                    <SelectTrigger
                      id="service"
                      className="h-14 text-base bg-white"
                      data-testid="form-service-trigger"
                    >
                      <SelectValue placeholder={t.contact.placeholders.service} />
                    </SelectTrigger>
                    <SelectContent>
                      {t.services.items.map((s, i) => (
                        <SelectItem key={i} value={s.title} data-testid={`form-service-option-${i}`}>
                          {s.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <div className="md:col-span-2">
                <Field id="message" label={t.contact.labels.message}>
                  <Textarea
                    id="message"
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    placeholder={t.contact.placeholders.message}
                    rows={4}
                    className="text-base bg-white resize-none"
                    data-testid="form-message-input"
                  />
                </Field>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="mt-7 w-full h-14 bg-blue-900 hover:bg-blue-800 text-white text-lg font-semibold rounded-xl shadow-lg disabled:opacity-60"
              data-testid="form-submit-button"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  {t.contact.submitting}
                </>
              ) : (
                <>
                  <Send className="w-5 h-5 mr-2" />
                  {t.contact.submit}
                </>
              )}
            </Button>
          </form>
        </div>

        {/* Centres side by side */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8 mt-14">
          <CentreCard
            label={t.contact.info.hoursCampelloLabel}
            address={t.contact.info.address}
            addressRef={t.contact.info.addressReference}
            phones={[
              { href: "tel:+34965637618", text: "965 637 618" },
              { href: "tel:+34722797599", text: "+34 722 797 599" },
            ]}
            hours={t.contact.info.hoursCampello}
            mapSrc="https://www.google.com/maps?q=Avenida+de+la+Generalitat+50,+El+Campello,+Alicante&output=embed"
            mapTitle="Mapa Amplisoon El Campello"
            mapTestId="map-campello"
            testId="centre-campello"
          />
          <CentreCard
            label={t.contact.info.hoursTorrellanoLabel}
            address={t.contact.info.address2}
            addressRef={t.contact.info.address2Reference}
            phones={[
              { href: "tel:+34965682209", text: "965 682 209" },
              { href: "tel:+34644258557", text: "+34 644 258 557" },
            ]}
            hours={t.contact.info.hoursTorrellano}
            mapSrc="https://www.google.com/maps?q=Avenida+Illice+57,+Torrellano,+Elche,+Alicante&output=embed"
            mapTitle="Mapa Amplisoon Torrellano"
            mapTestId="map-torrellano"
            testId="centre-torrellano"
          />
        </div>
      </div>
    </section>
  );
}

function CentreCard({ label, address, addressRef, phones, hours, mapSrc, mapTitle, mapTestId, testId }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 md:p-6 space-y-3 flex flex-col" data-testid={testId}>
      <div className="text-xs font-bold uppercase tracking-widest text-sky-700">{label}</div>
      <div className="flex gap-3 items-start">
        <MapPin className="w-5 h-5 text-sky-700 shrink-0 mt-1" />
        <div className="text-base text-slate-800 leading-relaxed">
          {address}
          <span className="block text-sm text-sky-700 font-semibold mt-0.5">{addressRef}</span>
        </div>
      </div>
      <div className="flex gap-3 items-start">
        <Phone className="w-5 h-5 text-sky-700 shrink-0 mt-1" />
        <div className="text-base text-slate-800">
          <a href={phones[0].href} className="hover:text-blue-900">{phones[0].text}</a>
          {" · "}
          <a href={phones[1].href} className="hover:text-blue-900">{phones[1].text}</a>
        </div>
      </div>
      <div className="flex gap-3 items-start" data-testid={`${testId}-email`}>
        <Mail className="w-5 h-5 text-sky-700 shrink-0 mt-1" />
        <a href={`mailto:${CONTACT_EMAIL}`} className="text-base text-slate-800 hover:text-blue-900 break-all">
          {CONTACT_EMAIL}
        </a>
      </div>
      <div className="flex gap-3 items-start">
        <Clock className="w-5 h-5 text-sky-700 shrink-0 mt-1" />
        <ul className="space-y-0.5 text-base text-slate-700">
          {hours.map((h, i) => (
            <li key={i}>{h}</li>
          ))}
        </ul>
      </div>
      <div className="rounded-xl overflow-hidden border border-slate-200 mt-auto">
        <iframe
          title={mapTitle}
          src={mapSrc}
          className="w-full h-56 border-0"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          data-testid={mapTestId}
        />
      </div>
    </div>
  );
}

function Field({ id, label, required, children }) {
  return (
    <div>
      <Label htmlFor={id} className="text-sm font-semibold text-slate-700 mb-2 block">
        {label} {required && <span className="text-rose-600">*</span>}
      </Label>
      {children}
    </div>
  );
}
